import { post } from "@src/appV2/api/api";
import { API_TYPES } from "@src/appV2/api/constants";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { z } from "zod";

import { type ESignWorkflowSessionResponse, eSignWorkflowSessionResponseSchema } from "./types";

const createESignWorkflowSessionSchema = z.object({
  data: z.object({
    type: z.literal(API_TYPES.eSignWorkflowSession),
    attributes: z.object({
      compositeParentRequirementId: z.string().optional(),
    }),
  }),
});

type CreateESignWorkflowSession = z.infer<typeof createESignWorkflowSessionSchema>;

interface UseCreateESignWorkflowSessionProps {
  requirementId: string;
}

export function useCreateESignWorkflowSession(
  props: UseCreateESignWorkflowSessionProps,
  options: UseMutationOptions<
    ESignWorkflowSessionResponse,
    AxiosError,
    CreateESignWorkflowSession["data"]["attributes"]
  > = {}
): UseMutationResult<
  ESignWorkflowSessionResponse,
  AxiosError,
  CreateESignWorkflowSession["data"]["attributes"]
> {
  return useMutation({
    mutationFn: async (variables: CreateESignWorkflowSession["data"]["attributes"]) => {
      const { requirementId } = props;

      const response = await post({
        url: `${environmentConfig.REACT_APP_DOCUMENTS_SERVICE_REST_API_URL}/requirements/${requirementId}/esign-workflow-sessions`,
        data: {
          data: {
            type: API_TYPES.eSignWorkflowSession,
            attributes: variables,
          },
        },
        requestSchema: createESignWorkflowSessionSchema,
        responseSchema: eSignWorkflowSessionResponseSchema,
      });

      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.CREATE_ESIGN_WORKFLOW_SESSION_FAILURE,
    },
    ...options,
  });
}
